/**
 * Centralized Lane Configuration
 * Single source of truth for all lane types, colors, flow detection, and styling
 */

// ============================================================================
// LANE TYPE CONFIGURATIONS
// ============================================================================

export const LANE_TYPES = {
  // FIRST LEG (Source → Distribution) - GREEN
  'plant-dc': {
    label: 'Plant → DC',
    description: 'Manufacturing to distribution center',
    category: 'first_leg',
    activeColor: '#4CAF50',   // GREEN - "go" signal, start of journey
    inactiveColor: '#BDBDBD', // GREY - unused lane
    activeWidth: 3,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-factory'
  },
  'supplier-dc': {
    label: 'Supplier → DC',
    description: 'Supplier to distribution center',
    category: 'first_leg',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 3,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-truck-delivery'
  },
  'copacker-dc': {
    label: 'Co-Packer → DC',
    description: 'Co-packer to distribution center',
    category: 'first_leg',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-package-variant'
  },
  
  // LAST LEG (Distribution → Customer) - PURPLE
  'dc-customer': {
    label: 'DC → Customer',
    description: 'Distribution center to customer',
    category: 'last_leg',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-store'
  },
  'plant-customer': {
    label: 'Plant → Customer',
    description: 'Direct plant to customer',
    category: 'last_leg',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-truck-fast'
  },
  'warehouse-customer': {
    label: 'Warehouse → Customer',
    description: 'Warehouse to customer',
    category: 'last_leg',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-warehouse'
  },
  
  // INTERMEDIARY FLOWS - BLUE
  'dc-dc': {
    label: 'DC → DC',
    description: 'Distribution center to distribution center',
    category: 'intermediary',
    activeColor: '#4CAF50',   // GREEN - neutral transfer
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-transfer'
  },
  'warehouse-dc': {
    label: 'Warehouse → DC',
    description: 'Warehouse to distribution center',
    category: 'intermediary',
    activeColor: '#4CAF50',   // GREEN
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true,
    icon: 'mdi-swap-horizontal'
  }
}

// ============================================================================
// HELPER FUNCTIONS - Centralized Lane Logic
// ============================================================================

/**
 * Get lane type key from lane object
 * @param {Object} lane - Lane object with origin_type and destination_type
 * @returns {string} - Lane type key (e.g., 'plant-dc', 'dc-customer')
 */
export function getLaneTypeKey(lane) {
  if (!lane) return 'dc-dc'
  
  const originType = (lane.origin_type || 'dc').toLowerCase()
  const destType = (lane.destination_type || lane.dest_type || 'dc').toLowerCase()
  
  return `${originType}-${destType}`
}

/**
 * Check if lane has flow - CENTRALIZED LOGIC
 * @param {Object} lane - Lane object
 * @returns {boolean} - True if lane has flow >= 1
 */
export function laneHasFlow(lane) {
  if (!lane) return false
  
  // Check multiple possible fields (handles different API responses):
  // 1. has_flow flag (boolean or 0/1)
  // 2. flow amount (number)
  // 3. flow_amount field (number)
  return lane.has_flow === true || 
         lane.has_flow === 1 || 
         (lane.flow != null && lane.flow >= 1) ||
         (lane.flow_amount != null && lane.flow_amount >= 1)
}

/**
 * Get total flow amount from lane
 * @param {Object} lane - Lane object
 * @returns {number} - Flow amount
 */
export function getLaneFlowAmount(lane) {
  if (!lane) return 0
  return lane.flow_amount || lane.flow || 0
}

/**
 * Get lane style configuration for a specific lane type
 * @param {Object} lane - Lane object
 * @returns {Object} - Lane style config
 */
export function getLaneStyle(lane) {
  const laneTypeKey = getLaneTypeKey(lane)
  
  return LANE_TYPES[laneTypeKey] || {
    activeColor: '#4CAF50',
    inactiveColor: '#BDBDBD',
    activeWidth: 2,
    inactiveWidth: 2,
    activeStyle: 'solid',
    inactiveStyle: 'dotted',
    showArrows: true
  }
}

/**
 * Get lane color from flow status and lane type.
 *
 * Service level is NOT part of this, and the `serviceLevelColor` option it
 * used to accept is gone. It was destructured and never referenced, so lane
 * service-level colouring had never coloured a lane; the caller in
 * `useMapRendering.js` now applies that colour itself, ahead of this, when the
 * user has the Service Level view on.
 *
 * @param {Object} lane - Lane object
 * @returns {string} - Hex color code
 */
export function getLaneColor(lane) {
  const hasFlow = laneHasFlow(lane)
  const laneStyle = getLaneStyle(lane)

  return hasFlow ? laneStyle.activeColor : laneStyle.inactiveColor
}

/**
 * Get lane width based on flow status
 * @param {Object} lane - Lane object
 * @returns {number} - Width in pixels
 */
export function getLaneWidth(lane) {
  const hasFlow = laneHasFlow(lane)
  const laneStyle = getLaneStyle(lane)
  
  return hasFlow ? laneStyle.activeWidth : laneStyle.inactiveWidth
}

/**
 * Get lane line style (solid/dotted)
 * @param {Object} lane - Lane object
 * @returns {string} - 'solid' or 'dotted'
 */
export function getLaneStyleType(lane) {
  const hasFlow = laneHasFlow(lane)
  const laneStyle = getLaneStyle(lane)
  
  return hasFlow ? laneStyle.activeStyle : laneStyle.inactiveStyle
}

/**
 * Filter lanes that have flow
 * @param {Array} lanes - Array of lane objects
 * @returns {Array} - Filtered lanes with flow
 */
export function filterLanesWithFlow(lanes) {
  return (lanes || []).filter(laneHasFlow)
}

/**
 * Get lane types by category
 * @param {string} category - 'first_leg', 'last_leg', or 'intermediary'
 * @returns {Array} - Array of lane type configs
 */
export function getLaneTypesByCategory(category) {
  return Object.entries(LANE_TYPES)
    .filter(([key, config]) => config.category === category)
    .map(([key, config]) => ({ key, ...config }))
}

/**
 * Get all lane type keys
 * @returns {Array} - Array of lane type keys
 */
export function getAllLaneTypeKeys() {
  return Object.keys(LANE_TYPES)
}

// ============================================================================
// BACKWARDS COMPATIBILITY
// ============================================================================

/**
 * Convert LANE_TYPES to old format (for components not yet migrated)
 * @returns {Object} - Lane settings in old byLaneType format
 */
export function getLaneSettingsCompatible() {
  return {
    byLaneType: LANE_TYPES
  }
}


